import * as React from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ThemeProvider, createTheme } from '@mui/material/styles';

const STORAGE_KEY = 'vending-machine-ui-theme';

const ThemeModeContext = React.createContext({
  mode: 'light',
  toggleColorMode: () => {},
});

export function useAppThemeMode() {
  return React.useContext(ThemeModeContext);
}

export default function AppThemeProvider({ children }) {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [mode, setMode] = React.useState(() => {
    const storedMode = localStorage.getItem(STORAGE_KEY);
    if (storedMode === 'dark' || storedMode === 'light') {
      return storedMode;
    }
    return prefersDarkMode ? 'dark' : 'light';
  });

  React.useEffect(() => {
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  const theme = React.useMemo(
    () =>
      createTheme({
        palette:
          mode === 'dark'
            ? {
                mode: 'dark',
                primary: { main: '#7dd3fc' },
                secondary: { main: '#fbbf24' },
                background: {
                  default: '#0b1120',
                  paper: '#111827',
                },
              }
            : {
                mode: 'light',
                primary: { main: '#0f172a' },
                secondary: { main: '#0284c7' },
                background: {
                  default: '#f8fafc',
                  paper: '#ffffff',
                },
              },
        shape: {
          borderRadius: 14,
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              html: { minHeight: '100%' },
              body: {
                minHeight: '100%',
                transition: 'background-color 180ms ease, color 180ms ease',
              },
              '#root': {
                minHeight: '100vh',
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
              },
            },
          },
          MuiButton: {
            defaultProps: {
              disableElevation: true,
            },
          },
        },
      }),
    [mode]
  );

  const value = React.useMemo(
    () => ({
      mode,
      toggleColorMode: () => {
        setMode((currentMode) => (currentMode === 'dark' ? 'light' : 'dark'));
      },
    }),
    [mode]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}
